import type { ReactNode } from 'react';
import StatusBadge from './StatusBadge';

type Props = {
  accent: 'violet' | 'cyan' | 'amber' | 'pink';
  status: 'live' | 'beta' | 'soon';
  title: string;
  tagline: string;
  body: string;
  link: string;
  icon: ReactNode;
};

const accentMap = {
  violet: {
    stripe: 'bg-violet',
    iconBg: 'bg-violetSoft',
    iconBorder: 'border-violet/30',
    tag: 'text-violet',
    link: 'text-violet hover:text-violetMid',
  },
  cyan: {
    stripe: 'bg-cyan',
    iconBg: 'bg-cyanSoft',
    iconBorder: 'border-cyan/30',
    tag: 'text-cyan-700 dark:text-cyan-300',
    link: 'text-cyan-700 dark:text-cyan-300 hover:opacity-80',
  },
  amber: {
    stripe: 'bg-amber',
    iconBg: 'bg-amberSoft',
    iconBorder: 'border-amber/30',
    tag: 'text-amber-700 dark:text-amber-300',
    link: 'text-amber-700 dark:text-amber-300 hover:opacity-80',
  },
  pink: {
    stripe: 'bg-pink',
    iconBg: 'bg-pinkSoft',
    iconBorder: 'border-pink/30',
    tag: 'text-pinkMid dark:text-pink-300',
    link: 'text-pinkMid dark:text-pink-300 hover:opacity-80',
  },
};

export default function PrimitiveCard({ accent, status, title, tagline, body, link, icon }: Props) {
  const a = accentMap[accent];
  return (
    <article className="group relative flex flex-col overflow-hidden rounded-2xl border border-border bg-surface shadow-hair transition-shadow duration-200 hover:shadow-elev">
      <div className={`h-[3px] w-full ${a.stripe}`} />
      <div className="flex flex-1 flex-col p-6">
        <header className="flex items-start justify-between">
          <div
            className={`grid size-10 place-items-center rounded-xl border ${a.iconBg} ${a.iconBorder}`}
          >
            {icon}
          </div>
          <StatusBadge status={status === 'live' ? 'live' : status === 'beta' ? 'beta' : 'soon'} />
        </header>
        <h3 className="mt-6 text-[22px] font-bold tracking-tighter text-ink">{title}</h3>
        <p className={`mt-2 text-[10px] font-bold uppercase tracking-wider ${a.tag}`}>{tagline}</p>
        <p className="mt-3 flex-1 text-[13px] leading-[1.55] text-body">{body}</p>
        <div className="mt-5 border-t border-slate100 pt-4">
          <a
            href="#"
            className={`inline-flex items-center gap-1 text-[12px] font-semibold transition-transform group-hover:translate-x-0.5 ${a.link}`}
          >
            {link} <span aria-hidden>→</span>
          </a>
        </div>
      </div>
    </article>
  );
}
